import { useState, type CSSProperties, type ReactNode } from 'react';
import { C } from '@ridgewill/brand';
import { useAsync } from '../lib/useAsync.js';
import { exact } from '../lib/format.js';
import {
  CATEGORY_LABELS,
  ESCALATION_LABELS,
  REPORT_CATEGORIES,
  STATUS_LABELS,
  TARGET_LABELS,
  filterSearch,
  type ReportCategory,
  type ReportCounts,
  type ReportFilter,
  type ReportListState,
  type ReportOutcome,
  type ReportOverview,
  type ReportRow,
} from '../lib/reports-api.js';
import { FilterChip, Screen } from '../components/Screen.js';
import { tabStyle } from '../components/Record.js';
import { AsyncBoundary, Badge, Button, EmptyState, Panel, When } from '../components/ui.js';
import { ReportBadge } from '../components/ReportBadge.js';
import { ReportCaseFile } from './ReportCaseFile.js';
import { api, PersonName } from './reports-shared.js';
import { EvidenceViewer } from './ReportEvidence.js';

/**
 * Support > Reports (support#87, Jon 2026-10-24): in-app reports of people and
 * of what they shared, kept apart from the ticket queue.
 *
 *   `#/reports`, `#/reports/overview`   the board: a row per category, every figure a link
 *   `#/reports/new`                     grouped by category, worst first
 *   `#/reports/escalated`               waiting on a permanent ban or a report to authorities
 *   `#/reports/archived`                handled, most recently archived first
 *   `#/reports/all`                     what a cross-state figure opens (a day's intake)
 *   `#/reports/R-0000123`               one report, as a case file
 *
 * Lists take the Overview's filters as a query (`?category=CHILD_SAFETY`,
 * `?outcome=permanent-ban&archivedWithin=30`, `?day=2026-10-20`), so a figure
 * and the list it opens are the same query.
 *
 * Jon chose the layout on 2026-10-24: the Overview as a departures board, a
 * report as a case file. The lists are grouped tables, the plainer of the two
 * registers, so the board stays the one place the page raises its voice.
 */
type Section = 'overview' | 'new' | 'escalated' | 'archived' | 'all';
const SECTIONS: Section[] = ['overview', 'new', 'escalated', 'archived', 'all'];

export const reportPath = (id: string) => `#/reports/${encodeURIComponent(id)}`;
export const listPath = (state: ReportListState, filter: ReportFilter = {}) => {
  const q = filterSearch(filter);
  return `#/reports/${state}${q ? `?${q}` : ''}`;
};

export function Reports({
  section: requested,
  detail,
  item,
  query,
  navigate,
}: {
  section?: string;
  /** `evidence` on `#/reports/R-0000123/evidence/<id>`: one image, in its own tab. */
  detail?: string;
  item?: string;
  query: Record<string, string>;
  navigate: (to: string) => void;
}) {
  const counts = useAsync(() => api.counts(), []);
  const reloadCounts = () => counts.reload(true);

  if (requested && /^R-\d+$/i.test(requested) && detail === 'evidence' && item) {
    return <EvidenceViewer key={item} reportId={requested.toUpperCase()} evidenceId={item} />;
  }
  if (requested && /^R-\d+$/i.test(requested)) {
    return <ReportCaseFile key={requested} id={requested.toUpperCase()} navigate={navigate} onChanged={reloadCounts} />;
  }
  const section: Section = SECTIONS.includes(requested as Section) ? (requested as Section) : 'overview';
  const tabs = <ReportTabs current={section} counts={counts.state.status === 'ready' ? counts.state.data : null} />;
  if (section === 'overview') return <OverviewScreen tabs={tabs} />;
  return <ListScreen key={`${section}?${JSON.stringify(query)}`} state={section} filter={filterOf(query)} tabs={tabs} navigate={navigate} />;
}

function filterOf(query: Record<string, string>): ReportFilter {
  const category = query.category?.toUpperCase();
  const outcome = query.outcome as ReportOutcome | undefined;
  const within = Number(query.archivedWithin);
  return {
    ...(category && (REPORT_CATEGORIES as readonly string[]).includes(category) ? { category: category as ReportCategory } : {}),
    ...(outcome && OUTCOME_LABELS[outcome] ? { outcome } : {}),
    ...(query.day && /^\d{4}-\d{2}-\d{2}$/.test(query.day) ? { day: query.day } : {}),
    ...(Number.isInteger(within) && within > 0 ? { archivedWithin: within } : {}),
  };
}

const OUTCOME_LABELS: Record<ReportOutcome, string> = {
  'content-removed': 'Content removed',
  'temporary-ban': 'Temporary ban',
  'permanent-ban': 'Permanent ban',
  authorities: 'Reported to authorities',
};

// ── Tabs, with the same badges as the nav ──────────────────────────────

/**
 * TabNav's look, plus counts: New carries the active count and blinks while a
 * child-safety report there is unopened; Escalated is always red (Jon,
 * 2026-10-24) and blinks while one has not been opened since it was escalated.
 */
function ReportTabs({ current, counts }: { current: Section; counts: ReportCounts | null }) {
  const tab = (path: Section, label: string, badge?: ReactNode) => (
    <a
      key={path}
      href={`#/reports/${path}`}
      aria-current={path === current ? 'page' : undefined}
      style={{ ...tabStyle(path === current), display: 'inline-flex', alignItems: 'center', gap: 8 }}
    >
      {label}
      {badge}
    </a>
  );
  return (
    <nav style={{ display: 'flex', gap: '.35rem', borderBottom: `1px solid ${C.border}`, paddingBottom: '.1rem', overflowX: 'auto' }} aria-label="Report sections">
      {tab('overview', 'Overview')}
      {tab('new', 'New', counts && counts.new > 0 ? <ReportBadge count={counts.new} urgent={counts.unviewedChildSafety.new > 0} /> : null)}
      {tab(
        'escalated',
        'Escalated',
        counts && counts.escalated > 0 ? (
          <ReportBadge count={counts.escalated} urgent={counts.unviewedChildSafety.escalated > 0} />
        ) : null,
      )}
      {tab('archived', 'Archived')}
    </nav>
  );
}

// ── The Overview: a departures board ───────────────────────────────────

function OverviewScreen({ tabs }: { tabs: ReactNode }) {
  const { state } = useAsync(() => api.overview(), []);
  return (
    <Screen filters={tabs}>
      <AsyncBoundary state={state}>{(o) => <Board overview={o} />}</AsyncBoundary>
    </Screen>
  );
}

const num: CSSProperties = { fontVariantNumeric: 'tabular-nums' };

function Count({ value, href, tone }: { value: number; href: string; tone?: 'danger' }) {
  if (value === 0) return <span style={{ ...num, color: C.mutedDot }}>0</span>;
  return (
    <a href={href} style={{ ...num, fontWeight: 700, fontSize: '1.05rem', color: tone === 'danger' ? C.dangerText : C.text }}>
      {value}
    </a>
  );
}

function Board({ overview: o }: { overview: ReportOverview }) {
  const childUnopened = o.counts.unviewedChildSafety.new + o.counts.unviewedChildSafety.escalated;
  const th: CSSProperties = {
    textAlign: 'left',
    padding: '.6rem 1.2rem',
    fontSize: '.72rem',
    letterSpacing: '.06em',
    textTransform: 'uppercase',
    color: C.textMid2,
    fontWeight: 640,
    borderBottom: `1px solid ${C.border}`,
    whiteSpace: 'nowrap',
  };
  const td: CSSProperties = { padding: '.85rem 1.2rem', borderBottom: `1px solid ${C.border2}`, whiteSpace: 'nowrap' };
  const right: CSSProperties = { textAlign: 'right' };

  return (
    <div style={{ display: 'grid', gap: '1rem' }}>
      <Panel
        title="Board"
        action={
          <span style={{ fontSize: '.84rem', color: C.textMid2 }}>
            <Count value={o.counts.active} href={listPath('new')} /> active
          </span>
        }
      >
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.9rem' }}>
            <thead>
              <tr>
                <th style={th}>Category</th>
                <th style={{ ...th, ...right }}>New</th>
                <th style={{ ...th, ...right }}>Escalated</th>
                <th style={th} className="rw-wide-only">Oldest waiting</th>
                <th style={{ ...th, ...right }} className="rw-wide-only">Archived, 30 days</th>
              </tr>
            </thead>
            <tbody>
              {o.byCategory.map((row) => {
                const lit = row.category === 'CHILD_SAFETY' && childUnopened > 0;
                const active = row.new + row.escalated > 0;
                return (
                  <tr key={row.category} style={lit ? { background: C.dangerBg } : undefined}>
                    <td style={{ ...td, whiteSpace: 'normal' }}>
                      {/* The dot stays beside the name; only "not yet opened" wraps under it on a phone. */}
                      <span style={{ display: 'flex', alignItems: 'baseline', gap: 9 }}>
                        <span
                          aria-hidden
                          className={lit ? 'rw-blink' : undefined}
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: 4,
                            flex: 'none',
                            transform: 'translateY(-1px)',
                            background: lit ? C.danger : active ? C.brand : C.border,
                          }}
                        />
                        <span style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', columnGap: 9, rowGap: 2 }}>
                          <span style={{ fontWeight: active ? 640 : 500, color: lit ? C.dangerText : active ? C.text : C.textMid2 }}>
                            {CATEGORY_LABELS[row.category]}
                          </span>
                          {lit ? (
                            <span style={{ fontSize: '.78rem', fontWeight: 640, color: C.dangerText }}>
                              {childUnopened} not yet opened
                            </span>
                          ) : null}
                        </span>
                      </span>
                    </td>
                    <td style={{ ...td, ...right }}>
                      <Count value={row.new} href={listPath('new', { category: row.category })} tone={lit ? 'danger' : undefined} />
                    </td>
                    <td style={{ ...td, ...right }}>
                      <Count value={row.escalated} href={listPath('escalated', { category: row.category })} tone="danger" />
                    </td>
                    <td style={{ ...td, color: C.textMid }} className="rw-wide-only">
                      {row.oldestWaitingAt ? (
                        <a href={listPath(row.new > 0 ? 'new' : 'escalated', { category: row.category })} style={{ color: 'inherit' }}>
                          <When iso={row.oldestWaitingAt} />
                        </a>
                      ) : (
                        <span style={{ color: C.mutedDot }}>None</span>
                      )}
                    </td>
                    <td style={{ ...td, ...right }} className="rw-wide-only">
                      <Count value={row.archived30Days} href={listPath('archived', { category: row.category, archivedWithin: 30 })} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Panel>

      <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))' }}>
        <Intake days={o.days} />
        <Panel title="Done in the last 30 days">
          <dl style={{ margin: 0, padding: '.4rem 1.2rem .8rem' }}>
            <Pair label="Archived" value={o.outcomes30Days.archived} href={listPath('archived', { archivedWithin: 30 })} />
            <Pair label="Content removed" value={o.outcomes30Days.contentRemoved} href={listPath('archived', { outcome: 'content-removed', archivedWithin: 30 })} />
            <Pair label="Temporary bans" value={o.outcomes30Days.temporaryBans} href={listPath('archived', { outcome: 'temporary-ban', archivedWithin: 30 })} />
            <Pair label="Permanent bans" value={o.outcomes30Days.permanentBans} href={listPath('archived', { outcome: 'permanent-ban', archivedWithin: 30 })} />
            <Pair label="Reported to authorities" value={o.outcomes30Days.reportedToAuthorities} href={listPath('archived', { outcome: 'authorities', archivedWithin: 30 })} />
          </dl>
        </Panel>
        <Panel title="Pace">
          <dl style={{ margin: 0, padding: '.4rem 1.2rem .8rem' }}>
            <Row label="Waiting longest">
              {o.oldestNew ? (
                <a href={reportPath(o.oldestNew.id)} style={{ color: C.text, fontWeight: 640 }}>
                  {o.oldestNew.id}, <When iso={o.oldestNew.reportedAt} />
                </a>
              ) : (
                <span style={{ color: C.mutedDot }}>Nothing new</span>
              )}
            </Row>
            <Row label="Until first opened">
              <a href={listPath('new')} style={{ color: C.text }}>{duration(o.medianToFirstView)}</a>
            </Row>
            <Row label="Until archived">
              <a href={listPath('archived', { archivedWithin: 30 })} style={{ color: C.text }}>{duration(o.medianToArchive)}</a>
            </Row>
            <p style={{ margin: '.5rem 0 0', fontSize: '.78rem', color: C.textMid2 }}>Middle values over reports filed in the last 30 days.</p>
          </dl>
        </Panel>
        <RepeatReported people={o.repeatReported} />
      </div>
    </div>
  );
}

function duration(ms: number | null): string {
  if (ms === null) return 'Not yet';
  const minutes = Math.round(ms / 60_000);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.round(minutes / 6) / 10;
  if (hours < 48) return `${hours} h`;
  return `${Math.round(hours / 24)} days`;
}

function Pair({ label, value, href }: { label: string; value: number; href: string }) {
  return (
    <Row label={label}>
      <Count value={value} href={href} />
    </Row>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '1rem', padding: '.5rem 0', borderBottom: `1px solid ${C.border2}` }}>
      <dt style={{ color: C.textMid }}>{label}</dt>
      <dd style={{ margin: 0, textAlign: 'right' }}>{children}</dd>
    </div>
  );
}

/** Fourteen days of intake. Each day is a link to that day's reports, in any state. */
function Intake({ days }: { days: ReportOverview['days'] }) {
  const max = Math.max(1, ...days.map((d) => d.total));
  return (
    <Panel title="Filed, last 14 days">
      <div style={{ padding: '1rem 1.2rem .8rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${days.length}, 1fr)`, gap: 4, alignItems: 'end', height: 110 }}>
          {days.map((d) => {
            const label = `${new Date(`${d.day}T12:00:00Z`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}: ${d.total} filed${d.childSafety ? `, ${d.childSafety} child safety` : ''}`;
            const bar = (n: number, color: string) => (n ? <span style={{ display: 'block', height: `${(n / max) * 100}%`, background: color }} /> : null);
            return d.total ? (
              <a
                key={d.day}
                href={listPath('all', { day: d.day })}
                title={label}
                aria-label={label}
                className="rw-navhover"
                style={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', borderRadius: 3, overflow: 'hidden' }}
              >
                {bar(d.total - d.childSafety, C.brand)}
                {bar(d.childSafety, C.danger)}
              </a>
            ) : (
              <span key={d.day} title={label} style={{ height: 2, background: C.border, alignSelf: 'end' }} />
            );
          })}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: '.74rem', color: C.textMid2 }}>
          <span>{shortDay(days[0]?.day)}</span>
          <span>Today</span>
        </div>
        <div style={{ display: 'flex', gap: 14, marginTop: 10, fontSize: '.78rem', color: C.textMid }}>
          <Key color={C.brand} label="Reports" />
          <Key color={C.danger} label="Child safety" />
          <span style={{ color: C.textMid2 }}>Each day opens its reports.</span>
        </div>
      </div>
    </Panel>
  );
}

const shortDay = (day?: string) =>
  day ? new Date(`${day}T12:00:00Z`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '';

function Key({ color, label }: { color: string; label: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
      <span aria-hidden style={{ width: 8, height: 8, borderRadius: 2, background: color }} />
      {label}
    </span>
  );
}

function RepeatReported({ people }: { people: ReportOverview['repeatReported'] }) {
  return (
    <Panel title="Reported more than once">
      {people.length === 0 ? (
        <EmptyState title="Nobody has two active reports." />
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: '.4rem 1.2rem .8rem' }}>
          {people.map((p) => (
            <li key={p.userId} style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', padding: '.5rem 0', borderBottom: `1px solid ${C.border2}` }}>
              <a href={`#/users/${encodeURIComponent(p.userId)}`} style={{ color: C.text, fontWeight: 600 }}>
                <PersonName id={p.userId} />
              </a>
              <span style={{ ...num, color: C.textMid }}>{p.active} active</span>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

// ── New, Escalated, Archived ───────────────────────────────────────────

const LIST_TITLES: Record<Exclude<Section, 'overview'>, string> = {
  new: 'New',
  escalated: 'Escalated',
  archived: 'Archived',
  all: 'All reports',
};

function ListScreen({
  state,
  filter,
  tabs,
  navigate,
}: {
  state: Exclude<Section, 'overview'>;
  filter: ReportFilter;
  tabs: ReactNode;
  navigate: (to: string) => void;
}) {
  const { state: page } = useAsync(() => api.list(state, filter), [state]);
  const chips = [
    filter.category ? CATEGORY_LABELS[filter.category] : null,
    filter.outcome ? OUTCOME_LABELS[filter.outcome] : null,
    filter.day ? `Filed ${shortDay(filter.day)}` : null,
    filter.archivedWithin ? `Archived in the last ${filter.archivedWithin} days` : null,
  ].filter((c): c is string => c !== null);

  return (
    <Screen
      filters={tabs}
      chips={
        chips.length ? (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {chips.map((c) => (
              <FilterChip key={c} label={c} onRemove={() => navigate(`/reports/${state === 'all' ? 'overview' : state}`)} />
            ))}
          </div>
        ) : undefined
      }
    >
      <AsyncBoundary state={page}>
        {(p) =>
          p.reports.length === 0 ? (
            <Panel title={LIST_TITLES[state]}>
              <EmptyState
                title={state === 'new' ? 'Nothing new.' : state === 'escalated' ? 'Nothing is waiting on an admin.' : 'No reports here.'}
                suggestion={chips.length ? 'Remove the filter to see every report in this list.' : undefined}
              />
            </Panel>
          ) : state === 'new' || state === 'escalated' ? (
            <Grouped reports={p.reports} escalated={state === 'escalated'} />
          ) : (
            <Flat reports={p.reports} state={state} filter={filter} more={p.hasMore ? p.nextCursor : null} />
          )
        }
      </AsyncBoundary>
    </Screen>
  );
}

const cellTh: CSSProperties = {
  textAlign: 'left',
  padding: '.55rem 1.2rem',
  fontSize: '.72rem',
  letterSpacing: '.06em',
  textTransform: 'uppercase',
  color: C.textMid2,
  fontWeight: 640,
  borderBottom: `1px solid ${C.border}`,
  whiteSpace: 'nowrap',
};
const cell: CSSProperties = { padding: '.7rem 1.2rem', borderBottom: `1px solid ${C.border2}`, verticalAlign: 'top' };

/** One table, a heading row per category, worst first: the order the server sent. */
function Grouped({ reports, escalated }: { reports: ReportRow[]; escalated: boolean }) {
  const groups = REPORT_CATEGORIES.map((c) => ({ category: c, rows: reports.filter((r) => r.category === c) })).filter((g) => g.rows.length);
  return (
    <section style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden' }}>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.9rem' }}>
          <thead>
            <tr>
              <th style={cellTh}>Report</th>
              <th style={cellTh}>About</th>
              <th style={cellTh}>{escalated ? 'Status' : 'Reporter said'}</th>
              <th style={cellTh}>Reported</th>
            </tr>
          </thead>
          {groups.map((g) => {
            const child = g.category === 'CHILD_SAFETY';
            return (
              <tbody key={g.category}>
                <tr>
                  <th
                    colSpan={4}
                    scope="colgroup"
                    style={{
                      textAlign: 'left',
                      padding: '.6rem 1.2rem',
                      background: child ? C.dangerBg : C.page,
                      color: child ? C.dangerText : C.text,
                      fontSize: '.84rem',
                      fontWeight: 700,
                      borderBottom: `1px solid ${child ? C.dangerBorder : C.border}`,
                    }}
                  >
                    {CATEGORY_LABELS[g.category]}
                    <span style={{ ...num, marginLeft: 8, fontWeight: 600, color: child ? C.dangerText : C.textMid2 }}>{g.rows.length}</span>
                  </th>
                </tr>
                {g.rows.map((r) => (
                  <ListRow key={r.id} report={r} escalated={escalated} />
                ))}
              </tbody>
            );
          })}
        </table>
      </div>
    </section>
  );
}

function ListRow({ report: r, escalated }: { report: ReportRow; escalated: boolean }) {
  const unopened = r.category === 'CHILD_SAFETY' && (escalated ? !r.escalationViewedAt : !r.viewedAt);
  return (
    <tr className="rw-rowhover">
      <td style={{ ...cell, whiteSpace: 'nowrap' }}>
        <a href={reportPath(r.id)} style={{ ...num, fontWeight: 700, color: C.text }}>
          {r.id}
        </a>
        <div style={{ fontSize: '.8rem', color: C.textMid2, marginTop: 2 }}>
          {TARGET_LABELS[r.targetKind]} in {r.product}
        </div>
        {unopened ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 4, fontSize: '.76rem', fontWeight: 700, color: C.dangerText }}>
            <span aria-hidden className="rw-blink" style={{ width: 7, height: 7, borderRadius: 4, background: C.danger }} />
            Not yet opened
          </span>
        ) : null}
      </td>
      <td style={cell}>
        <PersonName id={r.reportedUserId} />
        <Marks report={r} />
      </td>
      <td style={{ ...cell, color: C.textMid, maxWidth: 420 }}>
        {escalated && r.escalationReason ? (
          <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
            <Badge tone={r.legalHold ? 'danger' : 'neutral'}>{r.escalationStatus ? STATUS_LABELS[r.escalationStatus] : 'Escalated'}</Badge>
            <span style={{ fontSize: '.82rem', color: C.textMid2 }}>{ESCALATION_LABELS[r.escalationReason]}</span>
          </span>
        ) : r.detailsPreview ? (
          <span>{r.detailsPreview}</span>
        ) : (
          <span style={{ color: C.mutedDot }}>No note</span>
        )}
      </td>
      <td style={{ ...cell, whiteSpace: 'nowrap', color: C.textMid }} title={exact(r.reportedAt)}>
        <When iso={r.reportedAt} />
      </td>
    </tr>
  );
}

/** What has already been done, so a list shows where each one stands. */
function Marks({ report: r }: { report: ReportRow }) {
  const marks = [
    r.contentHidden ? 'Content removed' : null,
    r.temporaryBanUntil ? 'Temporary ban' : null,
    r.permanentlyBannedAt ? 'Banned permanently' : null,
    r.authoritiesReportedAt ? 'Reported to authorities' : null,
  ].filter((m): m is string => m !== null);
  if (marks.length === 0) return null;
  return <div style={{ fontSize: '.78rem', color: C.textMid2, marginTop: 3 }}>{marks.join(' · ')}</div>;
}

/** Archived and All: newest first, fifty at a time, with Show more appending the next page. */
function Flat({ reports: first, state, filter, more: firstMore }: { reports: ReportRow[]; state: 'archived' | 'all'; filter: ReportFilter; more: string | null }) {
  const [reports, setReports] = useState(first);
  const [more, setMore] = useState(firstMore);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const showMore = () => {
    if (!more) return;
    setLoading(true);
    setFailed(null);
    api
      .list(state, filter, more)
      .then((p) => {
        setReports((r) => [...r, ...p.reports]);
        setMore(p.hasMore ? p.nextCursor : null);
      })
      .catch((e: Error) => setFailed(e.message))
      .finally(() => setLoading(false));
  };
  return (
    <section style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden' }}>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.9rem' }}>
          <thead>
            <tr>
              <th style={cellTh}>Report</th>
              <th style={cellTh}>Category</th>
              <th style={cellTh}>About</th>
              <th style={cellTh}>Outcome</th>
              <th style={cellTh}>{state === 'archived' ? 'Archived' : 'Reported'}</th>
            </tr>
          </thead>
          <tbody>
            {reports.map((r) => (
              <tr key={r.id} className="rw-rowhover">
                <td style={{ ...cell, whiteSpace: 'nowrap' }}>
                  <a href={reportPath(r.id)} style={{ ...num, fontWeight: 700, color: C.text }}>
                    {r.id}
                  </a>
                  <div style={{ fontSize: '.8rem', color: C.textMid2, marginTop: 2 }}>{TARGET_LABELS[r.targetKind]}</div>
                </td>
                <td style={{ ...cell, color: r.category === 'CHILD_SAFETY' ? C.dangerText : C.textMid, fontWeight: r.category === 'CHILD_SAFETY' ? 640 : 400 }}>
                  {CATEGORY_LABELS[r.category]}
                </td>
                <td style={cell}>
                  <PersonName id={r.reportedUserId} />
                </td>
                <td style={{ ...cell, color: C.textMid }}>
                  {state === 'all' && r.state !== 'ARCHIVED' ? (
                    <Badge tone={r.state === 'ESCALATED' ? 'danger' : 'neutral'}>{r.state === 'NEW' ? 'New' : 'Escalated'}</Badge>
                  ) : (
                    <Marks report={r} />
                  )}
                  {r.state === 'ARCHIVED' && !r.contentHidden && !r.temporaryBanUntil && !r.permanentlyBannedAt && !r.authoritiesReportedAt ? (
                    <span style={{ color: C.textMid2 }}>No action</span>
                  ) : null}
                </td>
                <td style={{ ...cell, whiteSpace: 'nowrap', color: C.textMid }}>
                  <When iso={state === 'archived' ? r.archivedAt : r.reportedAt} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {more || failed ? (
        <div style={{ padding: '.8rem 1.2rem', display: 'flex', gap: '1rem', alignItems: 'center' }}>
          {more ? (
            <Button onClick={showMore} disabled={loading}>
              {loading ? 'Loading…' : 'Show more'}
            </Button>
          ) : null}
          {failed ? <span style={{ color: C.dangerText, fontSize: '.86rem' }}>{failed}</span> : null}
        </div>
      ) : null}
    </section>
  );
}

